import type { IRMetricKey } from '../../../type';
import type { IReportTable } from '../../types';

import { REPORT_METRIC_INFO, REPORT_METRIC_MAPPING } from '../../../ReportFilter';
import { BarChart, LineChart } from '~/presentation/reports/viz';
import { ReportChartSkeleton } from './ReportChartSkeleton';
import { CardAnalyticEmpty } from '~/modules/analytics';
import { BlockStack, Text } from '@shopify/polaris';
import { GTooltipCard } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { EReportChart } from '../../types';
import { useMemo } from 'react';
import { cls } from '~/utils';
export interface ReportChartProps {
  data: IReportTable;
  isLoading?: boolean;
  metricKey: IRMetricKey;
  chartType: EReportChart;
  dimensions: string[];
  addTimeDimension?: () => void;
}
export const ReportChart = (props: ReportChartProps) => {
  const { t } = useTranslation();

  const { metricKey, chartType, data, isLoading } = props;

  const { title, tooltip } = useMemo(() => {
    if (!metricKey) return { title: undefined, tooltip: undefined };

    return {
      title: REPORT_METRIC_MAPPING[metricKey]?.label,
      tooltip: REPORT_METRIC_INFO[metricKey as IRMetricKey],
    };
  }, [metricKey]);

  if (isLoading) {
    return <ReportChartSkeleton />;
  }

  const isNoData = !data.rows.length;

  const getChartComponent = () => {
    if (isNoData) {
      return (
        <CardAnalyticEmpty
          minHeight="332px"
          title={t('No data')}
          description={t('Try changing the filters')}
        />
      );
    }

    switch (chartType) {
      case EReportChart.LINE:
        return <LineChart {...props} />;
      case EReportChart.GROUP_BAR:
      case EReportChart.SIMPLE_BAR:
        return <BarChart {...props} chartType={chartType} />;
      default:
        return <LineChart {...props} />;
    }
  };

  return (
    <div
      className={cls('relative', 'p-4 pr-8', {
        'GAnalytic-Badge-Logo': !isNoData,
      })}
    >
      <BlockStack gap="400">
        <GTooltipCard tooltip={tooltip} alignment="start">
          <Text as="h3" variant="headingMd">
            {title}
          </Text>
        </GTooltipCard>
        {getChartComponent()}
      </BlockStack>
    </div>
  );
};
